/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import get from 'lodash/get';
import isNil from 'lodash/isNil';
import objectHash from 'object-hash';

import CommunicatorNumber from '../number/communicator-number';
import CommunicatorUtility from '../utility/communicator-utility';

/**
 * @private
 * @typedef {import('../../layer/layer-attrs').LayerAttrs} LayerAttrs
 * @typedef {import('../../layout/layout').Layout} Layout
 * @typedef {import('../../layout/layout-attrs').LayoutAttrs} LayoutAttrs
 */

/**
 * Komunikátor šablon pro Ki-Wi Server.
 */
class CommunicatorLayout {
  /**
   * Vygeneruje identifikátor vrstvy.
   * @param {LayoutAttrs['id']} parentLayoutId Identifikátor šablony, do které vrstva patří.
   * @param {number} index Pořadí vrstvy v šabloně.
   * @private
   * @returns {string} Identifikátor.
   */
  static generateId(parentLayoutId, index) {
    return objectHash({ index, parentLayoutId });
  }

  /**
   * Načte seznam šablon a zanořených vrstev.
   * @param {Object[]} jsonArray Část odpovědi na obsah zařízení se seznamem šablon.
   * @returns {Layout[]} Pole atributů pro Layout a Layers v Layout.layers.
   */
  static parseMany(jsonArray) {
    return jsonArray.flatMap((json) => {
      // Parsování mainLayout není kvůli odlišnostem od běžných layoutů
      // podporováno a navíc výsledek nikdo nepotřebuje, takže se přeskakuje.
      const id = json.id;
      if (id === 'mainLayout') {
        return [];
      }
      return [this.parseOne(json)];
    });
  }

  /**
   * Načte jednu šablonu a zanořené vrstvy.
   * @param {Object} json Část odpovědi na obsah zařízení se šablonou.
   * @returns {Layout} Atributy pro Layout a Layer v Layout.layers.
   */
  static parseOne(json) {
    if (json.id === 'mainLayout') {
      throw new TypeError('parsing mainLayout is not supported');
    }
    try {
      return {
        // Pozor, z Ki-Wi Serveru pro mainLayout chodí napevno '', což ale
        // není validní hodnota!
        backgroundColor: json.bgColor,
        backgroundMediumId: CommunicatorUtility.parseReference(
          // Prakticky pro médium na pozadí (resp. jen obrázek je podporovaný) chodí
          // `"bgImage":{"ref":{id:"42","type":"media"}}` nebo `"bgImage":{}`.
          /** @type {{ ref?: { id: string, type: 'media'}}} */
          (json.bgImage)?.ref,
          'media'
        ),
        id: json.id,
        layers: this.parseLayers(json),
        name: json.name,
        // Bohužel naprosto nepochopitelně mainLayout nejednotně s ostatními user layouty
        // nemá položku playLength (a možná i další, protože v Ki-Wi Serveru je to ve více
        // různých souborech generátorů, které nejsou udržovány v syncu), takže nejde mít
        // povinný playDuration po naparsování! Jako workaround se pro tento velmi speciální
        // případ rovnou vrátí 0 (místo historického null, které bylo kdoví jak ošetřeno),
        // aby byla položka playDuration po naparsování jednotně vždy povinná.
        // - https://gitlab.com/ki-wi/server/kiwi-server-app/-/issues/4987
        // Nakonec po zjištění, že se parsování mainLayout nepotřebuje, tak ošetřeno
        // explicitním testem nahoře, aby ve zbytku nemuselo být ošetření speciálně pro mainLayout.
        playDuration: CommunicatorUtility.parseTime(
          get(json, 'playLength.timeLength')
        )
      };
    } catch (error) {
      throw new TypeError(`parsing layout ${JSON.stringify(json)}: ${error}`);
    }
  }

  // TODO #555: Dočasně public kvůli testovatelnosti.
  /**
   * Načte vrstvu z JSON.
   * @param {Object} json Část odpovědi na obsah zařízení s vrstvou.
   * @param {LayoutAttrs['id']} parentLayoutId Identifikátor šablony, do které vrstva patří.
   * @param {number} index Pořadí vrstvy v šabloně.
   * @returns {LayerAttrs} Vrstva.
   */
  static parseLayer(json, parentLayoutId, index) {
    if (isNil(parentLayoutId)) {
      throw new TypeError('Missing parent layout id.');
    }
    return {
      // TODO #458: Vnitřní/zanořené šablony nejsou prozatím podporované, takže
      // se po parseReferenceContentAttrs (kde se mohl layoutId naparsovat)
      // explicitně nastaví na undefined.
      // Pozor, pro mainLayout chodí napevno jedna items s "type": "module",
      // což není absolutně podporované (na druhou stranu se to naparsuje jako prázdný
      // nepolymorfní obsah).
      ...CommunicatorUtility.parseReferenceContentAttrs(json.ref),
      layoutId: undefined,

      height: this.parsePercentage(json.height),
      id: this.generateId(parentLayoutId, index),
      index,
      left: this.parsePercentage(json.posX),
      parentLayoutId,
      top: this.parsePercentage(json.posY),
      width: this.parsePercentage(json.width),
      zIndex: json.posZ
    };
  }

  /**
   * Načte vrstvy šablony z JSON.
   * @param {Object} json Část odpovědi na obsah zařízení se šablonou.
   * @returns {LayerAttrs[]} Seznam vrstev.
   */
  static parseLayers(json) {
    if (json.id === 'mainLayout') {
      throw new TypeError('parsing mainLayout is not supported');
    }
    return /** @type {any[]} */ (json.items ?? []).map((item, index) =>
      this.parseLayer(item, json.id, index)
    );
  }

  /**
   * Načte počet procent v
   * [CSS formátu](https://developer.mozilla.org/en-US/docs/Web/CSS/percentage).
   * @param {string} json Část odpovědi na obsah zařízení s procenty.
   * @private
   * @returns {string} Počet procent.
   */
  static parsePercentage(json) {
    const number = CommunicatorNumber.fromStringOrNull(json);
    return `${number}%`;
  }
}

export default CommunicatorLayout;
